import React, { useState, useReducer, useCallback, useRef } from 'react';
import {
  NavBar,
  NavBarList,
  NavBarListItem,
  FormInputIcon,
  FormInput,
  IconSearch,
} from '@spotify-internal/encore-creator-web';
import { getEpisodesForTrailerSelection } from '../../../lib/Api/trailerSelection';
import { spacer32 } from '@spotify-internal/encore-foundation';
import { LoadingIndicator } from '../../Shared/LoadingIndicator';
import debounce from 'lodash/debounce';
import { Trailer, Episode, PaginatedEpisodes } from '../../../types';
import { SortColumn, SortOrder, SortColumnState } from './shared/types';
import InfiniteScroll from 'react-infinite-scroller';
import { ShortEpisodesTable } from './ShortEpisodesTable';
import { AllEpisodesTable } from './AllEpisodesTable';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';

type Props = {
  podcastId: string;
  handleEpisodeClick(episode: Episode): void;
  selectedEpisode?: Trailer;
  rightPaneEl: React.RefObject<HTMLDivElement>;
};

type ReducerState = {
  shortEpisodes: {
    episodes?: Episode[];
    total?: number;
    hasMore: boolean;
  };
  allEpisodes: {
    episodes?: Episode[];
    total?: number;
    hasMore: boolean;
  };
};

enum ReducerActionType {
  APPEND_SHORT_EPISODES = 'APPEND_SHORT_EPISODES',
  APPEND_ALL_EPISODES = 'APPEND_ALL_EPISODES',
  RESET_SHORT_EPISODES = 'RESET_SHORT_EPISODES',
  RESET_ALL_EPISODES = 'RESET_ALL_EPISODES',
}

type ReducerAction = {
  type: ReducerActionType;
  episodesResponse: PaginatedEpisodes;
};

enum Tab {
  SHORT,
  ALL,
}

function reducer(state: ReducerState, action: ReducerAction): ReducerState {
  switch (action.type) {
    case ReducerActionType.APPEND_SHORT_EPISODES: {
      const { episodes, totalEpisodes, currentPage, totalPages } = action.episodesResponse;
      return {
        ...state,
        shortEpisodes: {
          episodes: state.shortEpisodes.episodes
            ? /* istanbul ignore next */ [...state.shortEpisodes.episodes, ...episodes]
            : episodes,
          total: totalEpisodes,
          hasMore: currentPage < totalPages,
        },
      };
    }
    case ReducerActionType.APPEND_ALL_EPISODES: {
      const { episodes, totalEpisodes, currentPage, totalPages } = action.episodesResponse;
      return {
        ...state,
        allEpisodes: {
          episodes: state.allEpisodes.episodes
            ? /* istanbul ignore next */ [...state.allEpisodes.episodes, ...episodes]
            : episodes,
          total: totalEpisodes,
          hasMore: currentPage < totalPages,
        },
      };
    }
    case ReducerActionType.RESET_SHORT_EPISODES: {
      const { episodes, totalEpisodes, currentPage, totalPages } = action.episodesResponse;
      return {
        ...state,
        shortEpisodes: {
          episodes,
          total: totalEpisodes,
          hasMore: currentPage < totalPages,
        },
      };
    }
    case ReducerActionType.RESET_ALL_EPISODES: {
      const { episodes, totalEpisodes, currentPage, totalPages } = action.episodesResponse;
      return {
        ...state,
        allEpisodes: {
          episodes,
          total: totalEpisodes,
          hasMore: currentPage < totalPages,
        },
      };
    }

    /* istanbul ignore next */
    default:
      return state;
  }
}

export function EpisodeTabs(props: Props) {
  const { podcastId, handleEpisodeClick, selectedEpisode, rightPaneEl } = props;

  const [episodesState, dispatchEpisodes] = useReducer(reducer, {
    shortEpisodes: {
      episodes: undefined,
      total: 0,
      hasMore: true,
    },
    allEpisodes: {
      episodes: undefined,
      total: 0,
      hasMore: true,
    },
  });
  const [activeTabIndex, setActiveTabIndex] = useState<number>(Tab.SHORT);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [shortEpisodeSort, setShortEpisodeSort] = useState<SortColumnState>({
    column: SortColumn.PUBLISH_DATE,
    order: SortOrder.DESC,
  });
  const [allEpisodeSort, setAllEpisodeSort] = useState<SortColumnState>({
    column: SortColumn.PUBLISH_DATE,
    order: SortOrder.DESC,
  });

  const shortEpisodesScroller = useRef<{ pageLoaded: number }>(null);
  const allEpisodesScroller = useRef<{ pageLoaded: number }>(null);
  const intl = useIntl();

  const loadMoreShortEpisodes = async page => {
    try {
      const response = await getEpisodesForTrailerSelection(
        podcastId,
        page,
        true,
        undefined,
        shortEpisodeSort.column,
        shortEpisodeSort.order,
      );

      dispatchEpisodes({
        type: ReducerActionType.APPEND_SHORT_EPISODES,
        episodesResponse: response,
      });
    } catch {}
  };

  const loadMoreAllEpisodes = async page => {
    try {
      const response = await getEpisodesForTrailerSelection(
        podcastId,
        page,
        false,
        searchQuery,
        allEpisodeSort.column,
        allEpisodeSort.order,
      );

      dispatchEpisodes({ type: ReducerActionType.APPEND_ALL_EPISODES, episodesResponse: response });
    } catch {}
  };

  const debouncedEpisodeSearch = useCallback(
    debounce(async (query: string) => {
      try {
        const response = await getEpisodesForTrailerSelection(
          podcastId,
          1,
          false,
          query,
          allEpisodeSort.column,
          allEpisodeSort.order,
        );
        dispatchEpisodes({
          type: ReducerActionType.RESET_ALL_EPISODES,
          episodesResponse: response,
        });

        // force InfiniteScroll internal state to reset
        allEpisodesScroller.current!.pageLoaded = 1;
      } catch {}
    }, 500),
    [allEpisodeSort],
  );

  const handleSearchInputChange = e => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedEpisodeSearch(query.trim());
  };

  const handleShortEpisodesColumnSortClick = async (column: SortColumn) => {
    const sortOrder = sortOrderForColumn(shortEpisodeSort, column);

    setShortEpisodeSort({
      column,
      order: sortOrder,
    });
    const episodesResponse = await getEpisodesForTrailerSelection(
      podcastId,
      1,
      true,
      undefined,
      column,
      sortOrder,
    );
    dispatchEpisodes({
      type: ReducerActionType.RESET_SHORT_EPISODES,
      episodesResponse: episodesResponse,
    });

    // force InfiniteScroll internal state to reset
    shortEpisodesScroller.current!.pageLoaded = 1;
  };

  const handleAllEpisodesColumnSortClick = async (column: SortColumn) => {
    const sortOrder = sortOrderForColumn(allEpisodeSort, column);

    setAllEpisodeSort({
      column,
      order: sortOrder,
    });
    const episodesResponse = await getEpisodesForTrailerSelection(
      podcastId,
      1,
      false,
      searchQuery,
      column,
      sortOrder,
    );
    dispatchEpisodes({
      type: ReducerActionType.RESET_ALL_EPISODES,
      episodesResponse: episodesResponse,
    });

    // force InfiniteScroll internal state to reset
    allEpisodesScroller.current!.pageLoaded = 1;
  };

  const sortOrderForColumn = (sortColumn: SortColumnState, column: SortColumn) => {
    if (sortColumn.column === column) {
      /* istanbul ignore next */ return sortColumn.order === SortOrder.ASC
        ? SortOrder.DESC
        : SortOrder.ASC;
    } else {
      return SortOrder.DESC;
    }
  };

  const loader = <LoadingIndicator style={{ marginTop: spacer32 }} key={0} />;
  const navBar = [
    {
      key: 'short-episodes',
      label: intl.formatMessage(
        defineMessage({
          id: 'trailer-selection.tab-short-episodes',
          defaultMessage: 'Short Episodes',
        }),
      ),
      content: (
        <InfiniteScroll
          ref={shortEpisodesScroller}
          loadMore={loadMoreShortEpisodes}
          hasMore={episodesState.shortEpisodes.hasMore}
          loader={loader}
          useWindow={false}
          getScrollParent={() => rightPaneEl.current}
        >
          <ShortEpisodesTable
            episodes={episodesState.shortEpisodes.episodes}
            episodesTotal={episodesState.shortEpisodes.total}
            sort={shortEpisodeSort}
            selectedEpisode={selectedEpisode}
            handleEpisodeClick={handleEpisodeClick}
            handleColumnSortClick={handleShortEpisodesColumnSortClick}
            handleNoShortEpisodesLinkClick={() => {
              setActiveTabIndex(Tab.ALL);
            }}
          />
        </InfiniteScroll>
      ),
    },
    {
      key: 'all-episodes',
      label: intl.formatMessage(
        defineMessage({
          id: 'trailer-selection.tab-all-episodes',
          defaultMessage: 'All Episodes',
        }),
      ),
      content: (
        <>
          <FormInputIcon icon={IconSearch} style={{ margin: `${spacer32} 0`, width: '400px' }}>
            <FormInput
              type="text"
              placeholder={intl.formatMessage(
                defineMessage({
                  id: 'trailer-selection.search-placeholder',
                  defaultMessage: 'Search',
                }),
              )}
              value={searchQuery}
              onChange={handleSearchInputChange}
            />
          </FormInputIcon>
          <InfiniteScroll
            ref={allEpisodesScroller}
            loadMore={loadMoreAllEpisodes}
            hasMore={episodesState.allEpisodes.hasMore}
            loader={loader}
            useWindow={false}
            getScrollParent={() => rightPaneEl.current}
          >
            <AllEpisodesTable
              episodes={episodesState.allEpisodes.episodes}
              episodesTotal={episodesState.allEpisodes.total}
              sort={allEpisodeSort}
              selectedEpisode={selectedEpisode}
              handleEpisodeClick={handleEpisodeClick}
              handleColumnSortClick={handleAllEpisodesColumnSortClick}
            />
          </InfiniteScroll>
        </>
      ),
    },
  ];

  return (
    <NavBar
      list={
        <NavBarList>
          {navBar.map((item, index) => (
            <NavBarListItem
              key={item.key}
              active={index === activeTabIndex}
              label={item.label}
              onClick={function() {
                setActiveTabIndex(index);
              }}
            />
          ))}
        </NavBarList>
      }
    >
      {navBar[activeTabIndex].content}
    </NavBar>
  );
}
